import { useRef, useState, type ComponentProps } from 'react'

import { useLoadConfirmDefault } from '@ctcells/api'
import type { TrayLoad } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import type { ActionGate } from '../access.ts'
import { historyTargetText } from '../format.ts'

import {
  chambersInvalid,
  loadConfirmReport,
  type LoadConfirmForm,
  type LoadConfirmReport,
} from './loadConfirmReport.ts'

/** 신고 칸이 서는 모듈 종류 — 원심 C 트레이 반입만(계약 `loaded_chambers` 「원심 C 트레이만 · 빈 A·L 은 싣지 않는다」) */
const CHAMBER_REPORT_MODULE_TYPE = 'centrifuge'

/**
 * 🔴 **신고 칸은 원심 C 트레이 반입에만 선다**(eric 2026-10-29 「원래 원심분리기에 나와야 하는 거 아니야? 거기다가 챔버 시작하잖아」).
 *    가르는 값은 서버 응답의 모듈 종류(`moduleType` · `GET /modules/{id}/control` 의 `module.module_type`)다 — 화면이
 *    `module_id` · 자리 이름 글자로 짐작하지 않는다. 종류를 모르면(조회 전 · 실패 · 데모) 칸을 세우지 않고, 본문에도 싣지 않는다.
 *    ⚠ `expected_chambers` 는 배치 값이라 같은 배치의 염색기 · 어셈블러 건에도 온다 — 그것으로 가르지 마라.
 * 서버 기대 챔버 수가 있으면 미리 채우고 빈 칸의 자리 글자도 「예상 N」이다. 사람이 고치거나 비울 수 있다(2026-10-27).
 * 적재 확약 창 — 신고 칸(적재 챔버 수)을 **선택으로** 받는다(팀장 결정 2026-09-26).
 *
 * 🔴 **비우고 확약할 수 있다.** 신고는 선택이다 — 비우면 본문에 키가 없다(`loadConfirmReport`).
 * ⚠ **평형추 적재 칸은 없다**(2026-09-29 걷음). 서버 `LoadConfirmEnvelope` 에서 `counterweight_loaded` 가
 *   빠졌다 — 평형추(R″)는 C 트레이 전용 칸에 상주하는 기본 적재라 신고 대상이 아니다. 보내도 서버가 422 없이
 *   조용히 버린다. 되살리지 마라.
 * 🔴 **단추 게이트는 표의 단추와 같은 것을 먼저 본다**(끊김 → 권한 → 이미 확약 · 요청 중).
 *    창이 떠 있는 사이 스트림이 끊기면 여기서도 막힌다. 입력 위반은 누를 때 칸에 표시하고 전송하지 않는다.
 * 🔴 **성공해야 닫는다.** 실패하면 열어 둔다 — 적은 신고를 다시 적게 하지 않는다.
 * 덮개는 본문 범위(`scope="body"`)다 — 머리줄·상시 작업 줄(E-STOP)은 창이 떠 있어도 눌린다(07 §6).
 */
export function LoadConfirmModal({
  load,
  expectedChambers,
  moduleType,
  gate,
  error,
  onSubmit,
  onClose,
}: {
  load: TrayLoad
  expectedChambers?: number | null | undefined
  /** 그 적재가 선 모듈의 종류(서버 응답). `centrifuge` 일 때만 신고 칸이 선다 */
  moduleType?: string | null | undefined
  /** 표의 확약 단추와 같은 게이트. 🔴 이것이 입력 검사보다 먼저 선다 */
  gate: ActionGate
  error: string | undefined
  onSubmit: (report: LoadConfirmReport) => void
  onClose: () => void
}) {
  const [edited, setEdited] = useState<LoadConfirmForm | null>(null)
  const reportsChambers = moduleType === CHAMBER_REPORT_MODULE_TYPE
  const expected =
    expectedChambers != null && Number.isSafeInteger(expectedChambers) && expectedChambers >= 0
      ? expectedChambers
      : null
  // 서버 기본값이 늦게 도착해도 사람이 고친(또는 비운) 칸은 덮지 않는다.
  const form = edited ?? { chambers: expected === null ? '' : String(expected) }
  const root = useRef<HTMLDivElement>(null)
  const validation = useSubmitValidation((value: LoadConfirmForm) => ({
    chambers: chambersInvalid(value.chambers) ? t('reason.loadedChambersInvalid') : undefined,
  }))
  const chambers = validation.field('chambers')

  return (
    <Modal
      size="sm"
      footer={
        <>
          <Button onClick={onClose} size="pill">
            {t('action.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (gate.disabled) return
              // 🔴 칸이 없는 적재(염색기 · 어셈블러 …)는 신고 키를 싣지 않는다
              if (!reportsChambers) {
                onSubmit({})
                return
              }
              if (!validation.validate(form, root.current)) return
              const report = loadConfirmReport(form)
              if (report !== null) onSubmit(report)
            }}
            size="pill"
            variant="primary"
            {...gate}
          >
            {t('action.confirmLoad')}
          </Button>
        </>
      }
      onClose={onClose}
      scope="body"
      title={t('action.confirmLoad')}
    >
      {/* 🔴 설명 문장을 달지 않는다(§1-1). 대상을 값으로만 보인다 */}
      <ValueRows
        rows={[
          {
            key: 'point',
            label: t('panel.handoffPoint'),
            value: `${historyTargetText(load.module_id)} · ${load.point_id}`,
          },
        ]}
      />
      {reportsChambers ? (
        <div ref={root}>
          <Field {...chambers.fieldProps} label={t('field.loadedChambersReport')}>
            <TextInput
              {...chambers.inputProps}
              onChange={(chambers) => {
                const next = { chambers }
                setEdited(next)
                validation.change('chambers', next)
              }}
              placeholder={
                expected === null
                  ? t('value.notReported')
                  : t('value.expectedChambers', { count: expected })
              }
              /*
               * 🔴 `type="number"` 을 쓰지 않는다 — 브라우저가 읽지 못한 글자(`3e` 등)를 **빈 값**으로 넘겨
               *    「신고 안 함」으로 조용히 접힌다. 글자 그대로 받아 `loadConfirmReport` 가 위반을 가린다.
               */
              value={form.chambers}
            />
          </Field>
        </div>
      ) : null}
      {error === undefined ? null : (
        <p className="vissue" role="alert">
          {error}
        </p>
      )}
    </Modal>
  )
}

/** live 경계에서만 선택한다. demo·순수 화면은 부가 API를 호출하지 않는다. */
export function LoadConfirmWithDefault(props: ComponentProps<typeof LoadConfirmModal>) {
  const view = useLoadConfirmDefault(props.load)
  // 조회 중 · 실패면 둘 다 모른다 — 칸이 서지 않고(종류 모름) 기본값도 없다. 창 자체는 막지 않는다
  const known = view.isError || view.isPending ? undefined : view.data
  return (
    <LoadConfirmModal
      {...props}
      expectedChambers={known?.expectedChambers}
      moduleType={known?.moduleType}
    />
  )
}
